import React from "react";
import "./Home.css";
import "./Services.css";
import Footer from "./Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import Masonry from "react-masonry-css";
import { CircleArrowRight, CirclePlus, PlayCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ProtectedResourceItem = ({ children, navigateTo }) => {
	const { currentUser } = useAuth();
	const navigate = useNavigate();

	const handleClick = () => {
		if (!currentUser) {
			alert("Please log in to access resources");
			navigate("/login");
			return;
		}
		navigate(navigateTo);
	};

	return (
		<div onClick={handleClick} style={{ cursor: "pointer" }}>
			{children}
		</div>
	);
};

function Services() {
	const navigate = useNavigate();

	return (
		<div className="main-container">
			<div className="video-background">
				<img src="/images/services-home-bg.jpeg" alt="bridge" />
			</div>

			<section className="hero-section hero-section-services" id="home">
				<h1>
					Sibling Support <span>Redefined</span>
				</h1>
				<p className="hero-text">
					Sibling Kinect provides a structured and supportive environment that
					aims to empower siblings, foster understanding, and promote healing
					within the family unit.
				</p>
				<button className="cta-button" onClick={() => navigate("/contact")}>
					Contact Us
					<FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: "10px" }} />
				</button>
			</section>

			<section
				className="services-section services-section-services-main kinect-services"
				id="services"
			>
				<div className="services-container">
					<h2 className="services-title">Kinect Services</h2>

					<div className="service-item">
						<div className="service-number">1</div>
						<div className="service-content">
							<h3>Individual mentorship</h3>
							<h4>who has first-hand experience as the at-home sibling</h4>
							<p>
								For Tori, firsthand experience means truly understanding the
								role of being a sibling. Through Sibling Kinect, guidance on
								navigating challenges with a sibling is offered from a place of
								deep personal insight and empathy.
							</p>
						</div>
					</div>

					<div className="service-separator"></div>

					<div className="service-item reverse">
						<div className="service-content">
							<h3>Connection</h3>
							<h4>between the clinical team and at-home sibling</h4>
							<p>
								Throughout the therapeutic process for the sibling away from
								home, various teams are involved, such as the family therapist,
								individual therapist, clinical team, and mentor staff, along
								with other program-specific teams. With Sibling Kinect, these
								professionals receive valuable insights from the at-home
								sibling's perspective, providing a fuller understanding of the
								family dynamics.
							</p>
							<p>
								Additionally, at-home siblings can have direct access to the
								family therapist at the program, ensuring their voice is heard.
								This integration is vital for involving the entire family in the
								therapeutic process, allowing for a more holistic and complete
								healing experience that includes the sibling as an essential
								part of the journey.
							</p>
						</div>
						<div className="service-number">2</div>
					</div>

					<div className="service-separator reverse-separator"></div>

					<div className="service-item">
						<div className="service-number">3</div>
						<div className="service-content">
							<h3>Access to one-of-a-kind resources</h3>
							<h4>
								intended to provide a greater understanding of mental health
							</h4>
							<p>
								As part of the Sibling Kinect community, members gain exclusive
								access to our website, which offers a variety of valuable
								resources. This includes a comprehensive mental health
								dictionary that simplifies diagnoses from the DSM (Diagnostic
								and Statistical Manual of Mental Disorders) to make them easily
								understandable for both adults and children.
							</p>
							<p>
								Members can engage with a unique interactive game designed to
								help users empathize with and better understand how individuals
								experience different diagnoses. The site also features blogs and
								video posts, providing further insights and support.
							</p>
						</div>
					</div>

					<div className="service-separator"></div>

					<div className="service-item reverse">
						<div className="service-content">
							<h3>Easier transition</h3>
							<h4>
								towards the end of therapeutic treatment for the family unit to
								re-acclimate
							</h4>
							<p>
								By integrating the at-home sibling into the treatment team's
								process, therapy naturally becomes part of their life even
								before the sibling in treatment returns home. This proactive
								involvement ensures a smoother transition, as shifts in family
								communication and dynamics will have already begun during the
								treatment period.
							</p>
							<p>
								As a result, the at-home sibling is not forced to adjust
								abruptly or unfairly to changes once their sibling returns,
								creating a more harmonious and supportive family environment.
							</p>
						</div>
						<div className="service-number">4</div>
					</div>

					<div className="service-separator reverse-separator"></div>

					<div className="service-item">
						<div className="service-number">5</div>
						<div className="service-content">
							<h3>Reeducating the entire family</h3>
							<h4>
								on how to resolve past traumas and create a new normal within
								their homes
							</h4>
							<p>
								Family history plays a crucial role in the therapeutic process.
								Understanding and learning from the past while creating new,
								positive experiences is central to Sibling Kinect’s mission.
								This involves revisiting the past through constructive
								conversations, enabling families to confront and grow from their
								challenges together.
							</p>
							<p>
								While much of this work takes place in family therapy during
								treatment, the true test lies in the transition back home. As
								old issues may resurface and trigger emotional responses in any
								family member, learning how to listen, support, and adapt during
								this period is essential for fostering healthy and enduring
								family relationships.
							</p>
						</div>
					</div>
				</div>
			</section>

			<section>
				<div className="Program-goal">
					<img
						src="/images/program-goal.jpg"
						alt="Bridge symbolizing connection between siblings during therapeutic treatment"
					/>
					<div>
						<h1>Program Goal</h1>
						<p>
							The primary goal of Sibling Kinect is to give a voice to siblings,
							bridging the gap between therapeutic programs and those at home.
							By partnering with Sibling Kinect, programs and therapeutic teams
							gain a deeper understanding of the sibling’s perspective — a
							crucial part of the family story that has often been overlooked.
						</p>
						<p>
							As part of the Sibling Kinect community, we are committed to
							ensuring that every sibling is heard and included in the process
							of healing and strengthening family relationships.
						</p>
					</div>
				</div>
			</section>

			<section className="resources-section" id="resources">
				<Masonry
					breakpointCols={{
						default: 4,
						1100: 3,
						700: 2,
						500: 1,
					}}
					className="resources-masonry-grid"
					columnClassName="resources-masonry-grid_column"
				>
					<div
						className="resource-item logo"
						style={{ height: "100px", marginTop: "20px" }}
					>
						<div className="logo-circle">
							<img
								src="/logo192.png"
								alt="Sibling Kinect Logo"
								className="resources-img"
							/>
						</div>
						<h3>Sibling Kinect</h3>
					</div>

					<ProtectedResourceItem navigateTo="/mental-health-game">
						<div
							className="resource-item"
							style={{
								height: "200px",
								backgroundImage: "url('/images/mental-health-game.jpeg')",
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
						>
							<h3 className="resource-item-title">
								Mental Health Awareness Game
							</h3>
							<CirclePlus className="icon" style={{ color: "#d9d2c3" }} />
						</div>
					</ProtectedResourceItem>

					<ProtectedResourceItem navigateTo="/videos">
						<div className="resource-item" style={{ height: "200px" }}>
							<h3>Videos</h3>
							<span className="resource-item-subtitle-line"></span>
							<p>Stay Tuned</p>
							<PlayCircle className="icon" />
						</div>
					</ProtectedResourceItem>

					<ProtectedResourceItem navigateTo="/newsletter">
						<div className="resource-item" style={{ height: "120px" }}>
							<h3>Newsletter</h3>
							<span className="resource-item-subtitle-line"></span>
							<p>Stay Tuned</p>
							<CirclePlus className="icon" />
						</div>
					</ProtectedResourceItem>

					<ProtectedResourceItem navigateTo="/resources">
						<div
							className="resource-item"
							style={{
								height: "280px",
								backgroundImage: "url('/images/resources-resource.jpeg')",
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
						>
							<h3 className="resource-item-title">Resources</h3>
							<CircleArrowRight className="icon" style={{ color: "#d9d2c3" }} />
						</div>
					</ProtectedResourceItem>

					<ProtectedResourceItem navigateTo="/dictionary">
						<div
							className="resource-item"
							style={{
								height: "200px",
								backgroundImage: "url('/images/newpaper.jpeg')",
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
						>
							<h3 className="resource-item-title">Mental Health Dictionary</h3>
							<CirclePlus className="icon" />
						</div>
					</ProtectedResourceItem>

					<ProtectedResourceItem navigateTo="/blog">
						<div className="resource-item" style={{ height: "200px" }}>
							<h3>Blogs</h3>
							<span className="resource-item-subtitle-line"></span>
							<p>Stay Tuned</p>
							<CirclePlus className="icon" />
						</div>
					</ProtectedResourceItem>

					<div
						className="resource-item image"
						style={{
							height: "320px",
							backgroundImage: "url('/images/childrenImage.jpeg')",
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					></div>
				</Masonry>
			</section>

			<section className="kinect-section">
				<div
					style={{
						height: "300px",
						width: "80%",
						backgroundImage: "url('/images/services-home-bg.jpeg')",
						backgroundSize: "cover",
						backgroundPosition: "center",
						borderRadius: "10px",
					}}
				>
					<h1>Let’s bridge the gap and get kinected!</h1>
					<button className="cta-button" onClick={() => navigate("/contact")}>
						Contact Us
						<FontAwesomeIcon
							icon={faArrowRight}
							style={{ marginLeft: "10px" }}
						/>
					</button>
				</div>
			</section>
			<Footer />
		</div>
	);
}

export default Services;
